import { useConfig } from "$global-providers/config-provider/provider";
import {
  type EpocusEncounter,
  useRxCollectionEpocusEncounter,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { useUser } from "$global-providers/user-provider/provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import { useCallback } from "react";
import { demoStudyPool } from "./demo-study-pool";

const STUDIES_PER_SEED = 3;

const MALE_FIRST_NAMES = [
  "Chidi",
  "Samuel",
  "Tunde",
  "Emeka",
  "Musa",
  "Kwame",
  "Obi",
] as const;

const FEMALE_FIRST_NAMES = [
  "Amara",
  "Ifeoma",
  "Ngozi",
  "Fatima",
  "Adaeze",
  "Zainab",
  "Chiamaka",
] as const;

const SURNAMES = [
  "Nwosu",
  "Balogun",
  "Eze",
  "Adeyemi",
  "Okafor",
  "Danjuma",
  "Oyelaran",
  "Abubakar",
] as const;

const OPERATOR_TITLES = ["Doctor", "Nurse", "Sonographer"] as const;
const DEVICE_IDS = [
  "echonous-kosmos-torso-one",
  "echonous-kosmos-lexsa",
] as const;

const pick = <T>(items: readonly T[]): T =>
  items[Math.floor(Math.random() * items.length)];

const randomInt = (min: number, max: number) =>
  min + Math.floor(Math.random() * (max - min + 1));

type UndocumentedStudy = {
  patientName: string | null;
  patientDob: string | null;
  patientGender: string | null;
  operatorName: string;
  accessionNumber: string | null;
  deviceId: string;
  minutesAgo: number;
};

const randomDob = () =>
  `${randomInt(1945, 2010)}-${String(randomInt(1, 12)).padStart(2, "0")}-${String(
    randomInt(1, 28),
  ).padStart(2, "0")}`;

/**
 * `identified` studies carry demographics and an accession number; the rest
 * mimic a portable Vscan Air export, which has no MRN or RIS to draw from and
 * so arrives with neither. A seed always produces at least one of each so both
 * To Document card variants show up.
 */
const randomStudy = ({ identified }: { identified: boolean }) => {
  const gender = pick(["M", "F"] as const);
  const firstName = pick(
    gender === "M" ? MALE_FIRST_NAMES : FEMALE_FIRST_NAMES,
  );
  const patientName = `${firstName} ${pick(SURNAMES)}`;

  return {
    patientName: identified ? patientName : null,
    patientDob: identified ? randomDob() : null,
    patientGender: identified ? gender : null,
    operatorName: `${pick(OPERATOR_TITLES)} ${pick(SURNAMES)}`,
    accessionNumber: identified ? `ACC-${randomInt(10_000, 99_999)}` : null,
    deviceId: pick(DEVICE_IDS),
    minutesAgo: randomInt(5, 600),
  } satisfies UndocumentedStudy;
};

const randomStudies = (): UndocumentedStudy[] =>
  Array.from({ length: STUDIES_PER_SEED }, (_unused, index) =>
    randomStudy({ identified: index > 0 }),
  );

export type SeedUndocumentedResult =
  | { outcome: "seeded"; count: number }
  | { outcome: "unconfigured" }
  | { outcome: "unavailable" };

/**
 * IMPORTED + unclaimed is the shape a study has when it lands from a scanner
 * but hasn't been matched to a clinician.
 *
 * Seeded rows carry the caller's own probe serial, which is what puts them in
 * their To Document queue. With serial-restricted triage off there is no serial
 * to stamp, and the rows land in the shared unmatched pool instead.
 *
 * Each seeded encounter adopts a real demo study's `dicomStudyId`, so imaging
 * instance replication supplies genuine server-minted instances and their asset
 * URLs sign. How many images an encounter shows is whatever that study holds.
 */
export const useSeedUndocumentedStudies = () => {
  const user = useUser();
  const collection = useRxCollectionEpocusEncounter();
  const { territory, environment } = useConfig();

  const seed = useCallback(
    async (
      deviceSerialNumber: string | null,
    ): Promise<SeedUndocumentedResult> => {
      if (!collection) return { outcome: "unavailable" };

      const demoStudyIds = demoStudyPool(territory, environment);
      if (demoStudyIds.length === 0) return { outcome: "unconfigured" };

      const now = Date.now();
      const MINUTE_MS = 60 * 1000;

      const encounters = randomStudies().map<EpocusEncounter>(
        (study, index) => {
          const date = new Date(
            now - study.minutesAgo * MINUTE_MS,
          ).toISOString();

          return {
            id: randomUniqueId(),
            status: "imported",
            performerId: null,
            elephantPatientId: null,
            patientName: study.patientName,
            patientDob: study.patientDob,
            patientGender: study.patientGender,
            operatorName: study.operatorName,
            accessionNumber: study.accessionNumber,
            dicomStudyId: demoStudyIds[index % demoStudyIds.length],
            deviceId: study.deviceId,
            deviceSerialNumber,
            location: null,
            date,
            meta: {
              createdAt: date,
              createdByUserId: user.id,
              updatedAt: date,
              updatedByUserId: user.id,
            },
          };
        },
      );

      // bulkInsert resolves rather than throwing on per-document failures, so
      // count what actually landed instead of assuming the whole batch did.
      const { success } = await collection.bulkInsert(encounters);

      return { outcome: "seeded", count: success.length };
    },
    [collection, territory, environment, user.id],
  );

  return { seed };
};
